import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Divider,
    Switch,
    FormControlLabel,
    FormGroup,
    Button,
    Grid,
    Card,
    CardContent,
    CardActions,
    Alert,
    Snackbar,
    CircularProgress,
    Tooltip,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    Notifications as NotificationsIcon,
    Info as InfoIcon,
    CheckCircle as SuccessIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    Save as SaveIcon,
    Refresh as ResetIcon,
    ExpandMore as ExpandMoreIcon,
    Help as HelpIcon,
    BugReport as DebugIcon
} from '@mui/icons-material';
import PageHeader from '../components/common/PageHeader';
import api from '../services/api';
import { useTranslation } from 'react-i18next';
/**
 * Page de gestion des préférences de notifications
 */
const NotificationPreferencesPage = () => {
    // Référence pour suivre les mises à jour d'état
    const preferencesRef = useRef(null);

    // État pour forcer le rendu
    const [renderKey, setRenderKey] = useState(0);

    const [preferences, setPreferences] = useState({
        email: {
            enabled: true,
            frequency: 'immediate', // immediate, daily, weekly
            types: {
                info: true,
                success: true,
                warning: true,
                error: true
            }
        },
        push: {
            enabled: true,
            types: {
                info: true,
                success: true,
                warning: true,
                error: true
            }
        },
        inApp: {
            enabled: true,
            types: {
                info: true,
                success: true,
                warning: true,
                error: true
            }
        },
        categories: {
            system: true,
            account: true,
            security: true,
            properties: true,
            messages: true
        }
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [expanded, setExpanded] = useState('panel1');
    const [debugMode, setDebugMode] = useState(false);

    // Mettre à jour la référence quand les préférences changent
    useEffect(() => {
        preferencesRef.current = preferences;
        console.log('Préférences mises à jour:', preferences);
    }, [preferences]);

    // Charger les préférences au chargement de la page
    useEffect(() => {
        loadPreferences();
    }, []);
    /**
     * Charger les préférences de notifications
     */
    const loadPreferences = async () => {
        try {
            setLoading(true);
            setError(null);

            console.log('Chargement des préférences...');

            // Appel API pour récupérer les préférences
            const response = await api.get('/notifications/preferences');

            console.log('Réponse API (chargement):', response.data);

            if (response.data.success) {
                // Vérifier que la structure est correcte
                const loadedPrefs = response.data.preferences;

                if (!loadedPrefs ||
                    !loadedPrefs.email?.types ||
                    !loadedPrefs.push?.types ||
                    !loadedPrefs.inApp?.types) {
                    console.warn('Structure de préférences invalide reçue de l\'API:', loadedPrefs);

                    // Utiliser une structure valide en fusionnant avec les valeurs par défaut
                    const mergedPreferences = {
                        ...preferences,
                        email: {
                            ...preferences.email,
                            ...(loadedPrefs?.email || {}),
                            types: {
                                ...preferences.email.types,
                                ...(loadedPrefs?.email?.types || {})
                            }
                        },
                        push: {
                            ...preferences.push,
                            ...(loadedPrefs?.push || {}),
                            types: {
                                ...preferences.push.types,
                                ...(loadedPrefs?.push?.types || {})
                            }
                        },
                        inApp: {
                            ...preferences.inApp,
                            ...(loadedPrefs?.inApp || {}),
                            types: {
                                ...preferences.inApp.types,
                                ...(loadedPrefs?.inApp?.types || {})
                            }
                        },
                        categories: {
                            ...preferences.categories,
                            ...(loadedPrefs?.categories || {})
                        }
                    };

                    console.log('Préférences fusionnées:', mergedPreferences);
                    setPreferences(mergedPreferences);
                } else {
                    console.log('Préférences valides reçues:', loadedPrefs);
                    setPreferences(loadedPrefs);
                }

                // Forcer un rendu pour s'assurer que l'UI est à jour
                setRenderKey(prev => prev + 1);
            } else {
                throw new Error(response.data.message || 'Erreur lors du chargement des préférences');
            }
        } catch (err) {
            console.error('Error loading notification preferences:', err);
            setError('Impossible de charger vos préférences de notifications: ' + (err.message || ''));
        } finally {
            setLoading(false);
        }
    };

    /**
     * Sauvegarder les préférences de notifications
     */
    const savePreferences = async () => {
        try {
            setSaving(true);
            setError(null);
            setSuccess(false);

            console.log('Sauvegarde des préférences:', preferences);

            // Vérifier que la structure est correcte avant d'envoyer
            if (!preferences.email?.types || !preferences.push?.types || !preferences.inApp?.types) {
                throw new Error('Structure de préférences invalide');
            }

            // Appel API pour sauvegarder les préférences
            const response = await api.post('/notifications/preferences', {
                preferences: JSON.parse(JSON.stringify(preferences)) // Deep clone pour éviter les références
            });

            console.log('Réponse API (sauvegarde):', response.data);

            if (response.data.success) {
                // Si l'API renvoie les préférences mises à jour, les utiliser
                if (response.data.preferences) {
                    console.log('Préférences retournées par l\'API:', response.data.preferences);
                    setPreferences(response.data.preferences);

                    // Forcer un rendu
                    setRenderKey(prev => prev + 1);
                }

                setSuccess(true);
            } else {
                throw new Error(response.data.message || 'Erreur lors de la sauvegarde');
            }
        } catch (err) {
            console.error('Error saving notification preferences:', err);
            setError('Impossible de sauvegarder vos préférences: ' + (err.message || ''));
        } finally {
            setSaving(false);
        }
    };

    /**
     * Réinitialiser les préférences de notifications
     */
    const resetPreferences = async () => {
        try {
            setSaving(true);
            setError(null);
            setSuccess(false);

            console.log('Réinitialisation des préférences...');

            // Appel API pour réinitialiser les préférences
            const response = await api.post('/notifications/preferences/reset');

            console.log('Réponse API (réinitialisation):', response.data);

            if (response.data.success) {
                console.log('Préférences réinitialisées:', response.data.preferences);
                setPreferences(response.data.preferences);
                setSuccess(true);

                // Forcer un rendu
                setRenderKey(prev => prev + 1);
            } else {
                throw new Error(response.data.message || 'Erreur lors de la réinitialisation');
            }
        } catch (err) {
            console.error('Error resetting notification preferences:', err);
            setError('Impossible de réinitialiser vos préférences: ' + (err.message || ''));
        } finally {
            setSaving(false);
        }
    };

    /**
     * Mettre à jour les préférences
     */
    const handlePreferenceChange = (category, key, value) => {
        console.log(`Modification de ${category}.${key} à ${value}`);

        setPreferences(prev => {
            const newPreferences = {
                ...prev,
                [category]: {
                    ...prev[category],
                    [key]: value
                }
            };

            console.log('Nouvelles préférences:', newPreferences);
            return newPreferences;
        });

        // Forcer un rendu après la mise à jour
        setTimeout(() => setRenderKey(prev => prev + 1), 0);
    };

    /**
     * Mettre à jour les types de notifications
     */
    const handleTypeChange = (category, type, value) => {
        console.log(`Modification du type ${category}.types.${type} à ${value}`);

        // Créer une copie profonde pour éviter les problèmes de référence
        const newPreferences = JSON.parse(JSON.stringify(preferences));

        // Mettre à jour la valeur spécifique
        if (newPreferences[category] && newPreferences[category].types) {
            newPreferences[category].types[type] = value;

            console.log('Nouvelles préférences (après modification type):', newPreferences);
            setPreferences(newPreferences);

            // Forcer un rendu après la mise à jour
            setTimeout(() => setRenderKey(prev => prev + 1), 0);
        } else {
            console.error(`Structure invalide pour ${category}.types.${type}`);
        }
    };

    /**
     * Mettre à jour les catégories de notifications
     */
    const handleCategoryChange = (category, value) => {
        console.log(`Modification de la catégorie ${category} à ${value}`);

        setPreferences(prev => {
            const newPreferences = {
                ...prev,
                categories: {
                    ...prev.categories,
                    [category]: value
                }
            };

            console.log('Nouvelles préférences (après modification catégorie):', newPreferences);
            return newPreferences;
        });

        // Forcer un rendu après la mise à jour
        setTimeout(() => setRenderKey(prev => prev + 1), 0);
    };

    /**
     * Gérer le changement de panneau d'accordéon
     */
    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    /**
     * Fermer l'alerte de succès
     */
    const handleCloseSuccess = () => {
        setSuccess(false);
    };

    /**
     * Fermer l'alerte d'erreur
     */
    const handleCloseError = () => {
        setError(null);
    };

    /**
     * Afficher l'état actuel des préférences (débogage)
     */
    const handleDebug = () => {
        console.log('État actuel des préférences:', preferences);
        console.log('Référence des préférences:', preferencesRef.current);
        alert(JSON.stringify(preferences, null, 2));
    };

    /**
     * Activer/désactiver le mode débogage
     */
    const toggleDebugMode = () => {
        setDebugMode(prev => !prev);
    };
      const { t } = useTranslation();
    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }} key={renderKey}>
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'flex-start',
                mb: 2,
                py: 2,
                px: 3,
                borderRadius: 2,
                background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                color: 'white',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                    {t('navigation.notificationPreferences')}
                </Typography>
                <Typography variant="subtitle1" sx={{ mb: 1.5, color: 'white' }}>
                    {t('notifications.preferences.subtitle')}
                </Typography>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Paper sx={{ p: 3, mb: 4 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">{t('notifications.preferences.generalSettings')}</Typography>

                            <Box>
                                {/* Bouton de débogage */}
                                <IconButton
                                    color={debugMode ? "secondary" : "default"}
                                    onClick={toggleDebugMode}
                                    sx={{ mr: 1 }}
                                >
                                    <DebugIcon />
                                </IconButton>

                                {debugMode && (
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={handleDebug}
                                        sx={{ mr: 2 }}
                                    >
                                        Debug
                                    </Button>
                                )}

                                <Button
                                    variant="outlined"
                                    startIcon={<ResetIcon />}
                                    onClick={resetPreferences}
                                    disabled={saving}
                                    sx={{ mr: 2 }}
                                >
                                    {t('common.reset')}
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                                    onClick={savePreferences}
                                    disabled={saving}
                                >
                                    {t('common.save')}
                                </Button>
                            </Box>
                        </Box>

                        <Divider sx={{ mb: 3 }} />

                        <Accordion
                            expanded={expanded === 'panel1'}
                            onChange={handleAccordionChange('panel1')}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel1-content"
                                id="panel1-header"
                            >
                                <Typography variant="subtitle1" fontWeight="medium">{t('notifications.preferences.emailNotifications')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={preferences.email.enabled}
                                                onChange={(e) => handlePreferenceChange('email', 'enabled', e.target.checked)}
                                            />
                                        }
                                        label={t('notifications.preferences.receiveEmailNotifications')}
                                    />

                                    {preferences.email.enabled && (
                                        <Box sx={{ ml: 3, mt: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                {t('notifications.preferences.emailFrequency')}
                                            </Typography>

                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={preferences.email.frequency === 'immediate'}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                handlePreferenceChange('email', 'frequency', 'immediate');
                                                            }
                                                        }}
                                                    />
                                                }
                                                label={t('notifications.preferences.immediate')}
                                            />

                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={preferences.email.frequency === 'daily'}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                handlePreferenceChange('email', 'frequency', 'daily');
                                                            }
                                                        }}
                                                    />
                                                }
                                                label={t('notifications.preferences.dailySummary')}
                                            />

                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={preferences.email.frequency === 'weekly'}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                handlePreferenceChange('email', 'frequency', 'weekly');
                                                            }
                                                        }}
                                                    />
                                                }
                                                label={t('notifications.preferences.weeklySummary')}
                                            />

                                            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                                                {t('notifications.preferences.notificationTypes')}
                                            </Typography>

                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={preferences.email.types.info}
                                                                onChange={(e) => {
                                                                    console.log('Changement info email:', e.target.checked);
                                                                    handleTypeChange('email', 'info', e.target.checked);
                                                                }}
                                                            />
                                                        }
                                                        label={
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <InfoIcon color="info" sx={{ mr: 1 }} />
                                                                {t('common.info')} {debugMode && `(${preferences.email.types.info ? 'ON' : 'OFF'})`}
                                                            </Box>
                                                        }
                                                    />
                                                </Grid>

                                                <Grid item xs={12} sm={6}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={preferences.email.types.success}
                                                                onChange={(e) => {
                                                                    console.log('Changement success email:', e.target.checked);
                                                                    handleTypeChange('email', 'success', e.target.checked);
                                                                }}
                                                            />
                                                        }
                                                        label={
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <SuccessIcon color="success" sx={{ mr: 1 }} />
                                                                {t('common.success')} {debugMode && `(${preferences.email.types.success ? 'ON' : 'OFF'})`}
                                                            </Box>
                                                        }
                                                    />
                                                </Grid>

                                                <Grid item xs={12} sm={6}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={preferences.email.types.warning}
                                                                onChange={(e) => {
                                                                    console.log('Changement warning email:', e.target.checked);
                                                                    handleTypeChange('email', 'warning', e.target.checked);
                                                                }}
                                                            />
                                                        }
                                                        label={
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <WarningIcon color="warning" sx={{ mr: 1 }} />
                                                                {t('common.warning')} {debugMode && `(${preferences.email.types.warning ? 'ON' : 'OFF'})`}
                                                            </Box>
                                                        }
                                                    />
                                                </Grid>

                                                <Grid item xs={12} sm={6}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={preferences.email.types.error}
                                                                onChange={(e) => {
                                                                    console.log('Changement error email:', e.target.checked);
                                                                    handleTypeChange('email', 'error', e.target.checked);
                                                                }}
                                                            />
                                                        }
                                                        label={
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <ErrorIcon color="error" sx={{ mr: 1 }} />
                                                                {t('common.error')} {debugMode && `(${preferences.email.types.error ? 'ON' : 'OFF'})`}
                                                            </Box>
                                                        }
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    )}
                                </FormGroup>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            expanded={expanded === 'panel2'}
                            onChange={handleAccordionChange('panel2')}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel2-content"
                                id="panel2-header"
                            >
                                <Typography variant="subtitle1" fontWeight="medium">{t('notifications.preferences.pushNotifications')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={preferences.push.enabled}
                                                onChange={(e) => handlePreferenceChange('push', 'enabled', e.target.checked)}
                                            />
                                        }
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                {t('notifications.preferences.receivePushNotifications')}
                                                <Tooltip title={t('notifications.preferences.pushNotificationTooltip')}>
                                                    <IconButton size="small">
                                                        <HelpIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        }
                                    />

                                    {preferences.push.enabled && (
                                        <Box sx={{ ml: 3, mt: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                {t('notifications.preferences.notificationTypes')}
                                            </Typography>

                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={preferences.push.types.info}
                                                                onChange={(e) => {
                                                                    console.log('Changement info push:', e.target.checked);
                                                                    handleTypeChange('push', 'info', e.target.checked);
                                                                }}
                                                            />
                                                        }
                                                        label={
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <InfoIcon color="info" sx={{ mr: 1 }} />
                                                                {t('common.info')} {debugMode && `(${preferences.push.types.info ? 'ON' : 'OFF'})`}
                                                            </Box>
                                                        }
                                                    />
                                                </Grid>

                                                <Grid item xs={12} sm={6}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={preferences.push.types.success}
                                                                onChange={(e) => {
                                                                    console.log('Changement success push:', e.target.checked);
                                                                    handleTypeChange('push', 'success', e.target.checked);
                                                                }}
                                                            />
                                                        }
                                                        label={
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <SuccessIcon color="success" sx={{ mr: 1 }} />
                                                                {t('common.success')} {debugMode && `(${preferences.push.types.success ? 'ON' : 'OFF'})`}
                                                            </Box>
                                                        }
                                                    />
                                                </Grid>

                                                <Grid item xs={12} sm={6}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={preferences.push.types.warning}
                                                                onChange={(e) => {
                                                                    console.log('Changement warning push:', e.target.checked);
                                                                    handleTypeChange('push', 'warning', e.target.checked);
                                                                }}
                                                            />
                                                        }
                                                        label={
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <WarningIcon color="warning" sx={{ mr: 1 }} />
                                                                {t('common.warning')} {debugMode && `(${preferences.push.types.warning ? 'ON' : 'OFF'})`}
                                                            </Box>
                                                        }
                                                    />
                                                </Grid>

                                                <Grid item xs={12} sm={6}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={preferences.push.types.error}
                                                                onChange={(e) => {
                                                                    console.log('Changement error push:', e.target.checked);
                                                                    handleTypeChange('push', 'error', e.target.checked);
                                                                }}
                                                            />
                                                        }
                                                        label={
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <ErrorIcon color="error" sx={{ mr: 1 }} />
                                                                {t('common.error')} {debugMode && `(${preferences.push.types.error ? 'ON' : 'OFF'})`}
                                                            </Box>
                                                        }
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    )}
                                </FormGroup>
                            </AccordionDetails>
                        </Accordion><Accordion
                        expanded={expanded === 'panel3'}
                        onChange={handleAccordionChange('panel3')}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel3-content"
                            id="panel3-header"
                        >
                            <Typography variant="subtitle1" fontWeight="medium">{t('notifications.preferences.inAppNotifications')}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={preferences.inApp.enabled}
                                            onChange={(e) => handlePreferenceChange('inApp', 'enabled', e.target.checked)}
                                        />
                                    }
                                    label={t('notifications.preferences.receiveInAppNotifications')}
                                />

                                {preferences.inApp.enabled && (
                                    <Box sx={{ ml: 3, mt: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            {t('notifications.preferences.notificationTypes')}
                                        </Typography>

                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={preferences.inApp.types.info}
                                                            onChange={(e) => {
                                                                console.log('Changement info inApp:', e.target.checked);
                                                                handleTypeChange('inApp', 'info', e.target.checked);
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <InfoIcon color="info" sx={{ mr: 1 }} />
                                                            {t('common.info')} {debugMode && `(${preferences.inApp.types.info ? 'ON' : 'OFF'})`}
                                                        </Box>
                                                    }
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={preferences.inApp.types.success}
                                                            onChange={(e) => {
                                                                console.log('Changement success inApp:', e.target.checked);
                                                                handleTypeChange('inApp', 'success', e.target.checked);
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <SuccessIcon color="success" sx={{ mr: 1 }} />
                                                            {t('common.success')} {debugMode && `(${preferences.inApp.types.success ? 'ON' : 'OFF'})`}
                                                        </Box>
                                                    }
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={preferences.inApp.types.warning}
                                                            onChange={(e) => {
                                                                console.log('Changement warning inApp:', e.target.checked);
                                                                handleTypeChange('inApp', 'warning', e.target.checked);
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <WarningIcon color="warning" sx={{ mr: 1 }} />
                                                            {t('common.warning')} {debugMode && `(${preferences.inApp.types.warning ? 'ON' : 'OFF'})`}
                                                        </Box>
                                                    }
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={preferences.inApp.types.error}
                                                            onChange={(e) => {
                                                                console.log('Changement error inApp:', e.target.checked);
                                                                handleTypeChange('inApp', 'error', e.target.checked);
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <ErrorIcon color="error" sx={{ mr: 1 }} />
                                                            {t('common.error')} {debugMode && `(${preferences.inApp.types.error ? 'ON' : 'OFF'})`}
                                                        </Box>
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )}
                            </FormGroup>
                        </AccordionDetails>
                    </Accordion>
                        <Accordion
                            expanded={expanded === 'panel4'}
                            onChange={handleAccordionChange('panel4')}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel4-content"
                                id="panel4-header"
                            >
                                <Typography variant="subtitle1" fontWeight="medium">{t('notifications.preferences.notificationCategories')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <FormGroup>
                                    <Typography variant="body2" gutterBottom>
                                        {t('notifications.preferences.selectCategories')}
                                    </Typography>

                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                        <Grid item xs={12} sm={6}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={preferences.categories.system}
                                                        onChange={(e) => handleCategoryChange('system', e.target.checked)}
                                                    />
                                                }
                                                label={t('notifications.preferences.categories.system')}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={preferences.categories.account}
                                                        onChange={(e) => handleCategoryChange('account', e.target.checked)}
                                                    />
                                                }
                                                label={t('notifications.preferences.categories.account')}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={preferences.categories.security}
                                                        onChange={(e) => handleCategoryChange('security', e.target.checked)}
                                                    />
                                                }
                                                label={t('notifications.preferences.categories.security')}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={preferences.categories.properties}
                                                        onChange={(e) => handleCategoryChange('properties', e.target.checked)}
                                                    />
                                                }
                                                label={t('notifications.preferences.categories.properties')}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={preferences.categories.messages}
                                                        onChange={(e) => handleCategoryChange('messages', e.target.checked)}
                                                    />
                                                }
                                                label={t('notifications.preferences.categories.messages')}
                                            />
                                        </Grid>
                                    </Grid>
                                </FormGroup>
                            </AccordionDetails>
                        </Accordion>
                    </Paper>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<ResetIcon />}
                            onClick={resetPreferences}
                            disabled={saving}
                            sx={{ mr: 2 }}
                        >
                            {t('common.reset')}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                            onClick={savePreferences}
                            disabled={saving}
                        >
                            {t('common.save')}
                        </Button>
                    </Box>
                </>
            )}

            {/* Alertes de succès et d'erreur */}
            <Snackbar
                open={success}
                autoHideDuration={6000}
                onClose={handleCloseSuccess}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
                    {t('notifications.preferences.successMessage')}
                </Alert>
            </Snackbar>

            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={handleCloseError}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default NotificationPreferencesPage;